import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Mail, Plus, Edit, Trash2, Copy, Eye, Check, X, Loader2, Search,
  ChevronRight, ChevronDown, GripVertical, Image, Type, Square, 
  Columns, Link2, AlignLeft, AlignCenter, AlignRight, Bold, Italic,
  Palette, Lock, Unlock, Smartphone, Monitor, ArrowUp, ArrowDown,
  Settings2, ToggleLeft, ToggleRight, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { cn } from '../lib/utils';
import { SystemEmailDesigns } from '../components/SystemEmailDesigns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Block type definitions
const BLOCK_TYPES = [
  { type: 'header', label: 'Header', icon: Type, description: 'Large title text' },
  { type: 'logo', label: 'Logo', icon: Image, description: 'Organization logo' },
  { type: 'heading', label: 'Heading', icon: Type, description: 'Section heading' },
  { type: 'text', label: 'Text', icon: AlignLeft, description: 'Paragraph text' },
  { type: 'image', label: 'Image', icon: Image, description: 'Upload or link image' },
  { type: 'button', label: 'Button', icon: Square, description: 'Call to action' },
  { type: 'divider', label: 'Divider', icon: Square, description: 'Horizontal line' },
  { type: 'spacer', label: 'Spacer', icon: Square, description: 'Empty space' },
  { type: 'columns', label: 'Columns', icon: Columns, description: '2-3 column layout' },
  { type: 'social', label: 'Social Links', icon: Link2, description: 'Social media' },
  { type: 'contact', label: 'Contact Info', icon: Mail, description: 'Contact details' },
  { type: 'footer', label: 'Footer', icon: Type, description: 'Footer text' },
  { type: 'email_content', label: 'Email Content', icon: FileText, description: "The email's own text" },
];

// Merge fields available - will be fetched from API
const DEFAULT_MERGE_FIELDS = [
  { field: '{{participant_first_name}}', label: 'First Name' },
  { field: '{{participant_full_name}}', label: 'Full Name' },
  { field: '{{participant_id}}', label: 'Participant ID' },
  { field: '{{case_manager_name}}', label: 'Case Manager' },
  { field: '{{staff_name}}', label: 'Staff Name' },
  { field: '{{staff_position}}', label: 'Staff Position' },
  { field: '{{current_date}}', label: 'Current Date' },
  { field: '{{organization_name}}', label: 'Organization' },
];

// Default block content
const getDefaultBlock = (type) => {
  const id = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const base = { id, type, is_locked: false, styles: {} };
  
  switch (type) {
    case 'header':
      return { ...base, content: { text: 'Email Header' }, styles: { font_size: '28px', text_align: 'center', padding: '30px 20px' } };
    case 'logo':
      return { ...base, content: { url: '', alt: 'Logo' }, styles: { width: '200px', text_align: 'center', padding: '20px' }, is_locked: true };
    case 'heading':
      return { ...base, content: { text: 'Section Heading', level: 'h2' }, styles: { font_size: '24px', text_align: 'left', padding: '20px' } };
    case 'text':
      return { ...base, content: { text: 'Enter your message text here...' }, styles: { font_size: '16px', text_align: 'left', padding: '20px', line_height: '1.6' } };
    case 'image':
      return { ...base, content: { url: '', alt: 'Image', link: '' }, styles: { width: '100%', text_align: 'center', padding: '20px' } };
    case 'button':
      return { ...base, content: { text: 'Click Here', url: '#' }, styles: { text_align: 'center', padding: '20px', button_color: '#92298d', button_text_color: '#ffffff', border_radius: '4px', button_padding: '12px 24px' } };
    case 'divider':
      return { ...base, content: {}, styles: { line_color: '#dddddd', line_width: '1px', padding: '10px 20px' } };
    case 'spacer':
      return { ...base, content: {}, styles: { height: '30px' } };
    case 'columns':
      return { ...base, content: { columns: [{ blocks: [] }, { blocks: [] }] }, styles: { padding: '20px' } };
    case 'social':
      return { ...base, content: { links: { facebook: '', twitter: '', linkedin: '', instagram: '' } }, styles: { text_align: 'center', padding: '20px' }, is_locked: true };
    case 'contact':
      return { ...base, content: { address: '', phone: '', email: '', website: '' }, styles: { text_align: 'center', padding: '20px', font_size: '14px' }, is_locked: true };
    case 'footer':
      return { ...base, content: { text: '© 2026 The HELP Center. All rights reserved.' }, styles: { font_size: '12px', text_align: 'center', padding: '20px', background_color: '#f4f4f4', text_color: '#999999' }, is_locked: true };
    case 'email_content':
      return { ...base, content: { show_subject: true }, styles: { font_size: '16px', text_align: 'left', padding: '20px', line_height: '1.6' } };
    default:
      return base;
  }
};

// Block Editor Component
const BlockEditor = ({ block, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast, branding, mergeFields = DEFAULT_MERGE_FIELDS }) => {
  const [expanded, setExpanded] = useState(false);
  
  const updateContent = (key, value) => {
    onChange({ ...block, content: { ...block.content, [key]: value } });
  };
  
  const updateStyles = (key, value) => {
    onChange({ ...block, styles: { ...block.styles, [key]: value } });
  };
  
  const toggleLocked = () => {
    onChange({ ...block, is_locked: !block.is_locked });
  };
  
  const insertMergeField = (field, contentKey) => {
    const currentValue = block.content[contentKey] || '';
    updateContent(contentKey, currentValue + field);
  };

  const blockDef = BLOCK_TYPES.find(b => b.type === block.type);
  const Icon = blockDef?.icon || Square;

  return (
    <Card className={cn("mb-2 border", block.is_locked ? "border-amber-300 bg-amber-50/50" : "")}>
      <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <GripVertical className="h-4 w-4 text-muted-foreground" />
        <Icon className="h-4 w-4" />
        <span className="font-medium text-sm flex-1">{blockDef?.label || block.type}</span>
        {block.is_locked && <Lock className="h-3 w-3 text-amber-600" />}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={isFirst}>
            <ArrowUp className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={isLast}>
            <ArrowDown className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </div>
      
      {expanded && (
        <CardContent className="pt-0 space-y-4">
          {/* Lock toggle */}
          <div className="flex items-center justify-between py-2 border-b">
            <Label className="text-sm flex items-center gap-2">
              {block.is_locked ? <Lock className="h-4 w-4 text-amber-600" /> : <Unlock className="h-4 w-4 text-green-600" />}
              {block.is_locked ? 'Locked (Staff cannot edit)' : 'Editable by Staff'}
            </Label>
            <Switch checked={block.is_locked} onCheckedChange={toggleLocked} />
          </div>
          
          {/* Block-specific content editors */}
          {block.type === 'header' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Header Text</Label>
                <div className="flex gap-2">
                  <Input value={block.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm">+ Merge</Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2 max-h-64 overflow-y-auto">
                      {mergeFields.map(f => (
                        <button key={f.field} className="w-full text-left px-2 py-1 text-sm hover:bg-muted rounded" onClick={() => insertMergeField(f.field, 'text')}>
                          {f.isCustom && <span className="text-xs text-purple-600 mr-1">[Custom]</span>}
                          {f.label}
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          )}
          
          {block.type === 'logo' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Logo URL</Label>
                <Input value={block.content.url || ''} onChange={(e) => updateContent('url', e.target.value)} placeholder="Enter image URL or use organization logo" />
                {branding?.logo_url && (
                  <Button variant="link" size="sm" className="mt-1 h-auto p-0" onClick={() => updateContent('url', branding.logo_url)}>
                    Use organization logo
                  </Button>
                )}
              </div>
              <div>
                <Label className="text-xs">Alt Text</Label>
                <Input value={block.content.alt || ''} onChange={(e) => updateContent('alt', e.target.value)} />
              </div>
            </div>
          )}
          
          {block.type === 'heading' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Heading Text</Label>
                <div className="flex gap-2">
                  <Input value={block.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm">+ Merge</Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2 max-h-64 overflow-y-auto">
                      {mergeFields.map(f => (
                        <button key={f.field} className="w-full text-left px-2 py-1 text-sm hover:bg-muted rounded" onClick={() => insertMergeField(f.field, 'text')}>
                          {f.isCustom && <span className="text-xs text-purple-600 mr-1">[Custom]</span>}
                          {f.label}
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div>
                <Label className="text-xs">Level</Label>
                <Select value={block.content.level || 'h2'} onValueChange={(v) => updateContent('level', v)}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="h1">H1 (Largest)</SelectItem>
                    <SelectItem value="h2">H2</SelectItem>
                    <SelectItem value="h3">H3</SelectItem>
                    <SelectItem value="h4">H4 (Smallest)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          {block.type === 'text' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Text Content</Label>
                <div className="flex gap-2 mb-1">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm">+ Merge Field</Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2 max-h-64 overflow-y-auto">
                      {mergeFields.map(f => (
                        <button key={f.field} className="w-full text-left px-2 py-1 text-sm hover:bg-muted rounded" onClick={() => insertMergeField(f.field, 'text')}>
                          {f.isCustom && <span className="text-xs text-purple-600 mr-1">[Custom]</span>}
                          {f.label}
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                </div>
                <Textarea 
                  value={block.content.text || ''} 
                  onChange={(e) => updateContent('text', e.target.value)} 
                  rows={4}
                  placeholder="Enter your message..."
                />
              </div>
            </div>
          )}
          
          {block.type === 'image' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Image URL</Label>
                <Input value={block.content.url || ''} onChange={(e) => updateContent('url', e.target.value)} placeholder="https://..." />
              </div>
              <div>
                <Label className="text-xs">Alt Text</Label>
                <Input value={block.content.alt || ''} onChange={(e) => updateContent('alt', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Link URL (optional)</Label>
                <Input value={block.content.link || ''} onChange={(e) => updateContent('link', e.target.value)} placeholder="https://..." />
              </div>
            </div>
          )}
          
          {block.type === 'button' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Button Text</Label>
                <Input value={block.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Button URL</Label>
                <Input value={block.content.url || ''} onChange={(e) => updateContent('url', e.target.value)} placeholder="https://..." />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Button Color</Label>
                  <div className="flex gap-1">
                    <Input type="color" value={block.styles.button_color || '#92298d'} onChange={(e) => updateStyles('button_color', e.target.value)} className="w-12 h-8 p-1" />
                    <Input value={block.styles.button_color || '#92298d'} onChange={(e) => updateStyles('button_color', e.target.value)} className="flex-1 h-8" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Text Color</Label>
                  <div className="flex gap-1">
                    <Input type="color" value={block.styles.button_text_color || '#ffffff'} onChange={(e) => updateStyles('button_text_color', e.target.value)} className="w-12 h-8 p-1" />
                    <Input value={block.styles.button_text_color || '#ffffff'} onChange={(e) => updateStyles('button_text_color', e.target.value)} className="flex-1 h-8" />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {block.type === 'divider' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Line Color</Label>
                <div className="flex gap-1">
                  <Input type="color" value={block.styles.line_color || '#dddddd'} onChange={(e) => updateStyles('line_color', e.target.value)} className="w-12 h-8 p-1" />
                  <Input value={block.styles.line_color || '#dddddd'} onChange={(e) => updateStyles('line_color', e.target.value)} className="flex-1 h-8" />
                </div>
              </div>
            </div>
          )}
          
          {block.type === 'spacer' && (
            <div>
              <Label className="text-xs">Height</Label>
              <Input value={block.styles.height || '30px'} onChange={(e) => updateStyles('height', e.target.value)} placeholder="30px" />
            </div>
          )}
          
          {block.type === 'contact' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Address</Label>
                <Input value={block.content.address || ''} onChange={(e) => updateContent('address', e.target.value)} />
                {branding?.address && <Button variant="link" size="sm" className="h-auto p-0" onClick={() => updateContent('address', branding.address)}>Use org address</Button>}
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={block.content.phone || ''} onChange={(e) => updateContent('phone', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={block.content.email || ''} onChange={(e) => updateContent('email', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Website</Label>
                <Input value={block.content.website || ''} onChange={(e) => updateContent('website', e.target.value)} />
              </div>
            </div>
          )}
          
          {block.type === 'email_content' && (
            <div className="space-y-3" data-testid="email-content-block-settings">
              <p className="text-xs text-muted-foreground">
                When an email is sent with this design, this block is replaced by that email's own text: the Success Coaching subject and message, or the body of your email template.
              </p>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Show the email subject as a title</Label>
                <Switch
                  checked={block.content.show_subject !== false}
                  onCheckedChange={(v) => updateContent('show_subject', v)}
                  data-testid="email-content-show-subject-switch"
                />
              </div>
            </div>
          )}

          {block.type === 'footer' && (
            <div>
              <Label className="text-xs">Footer Text</Label>
              <Textarea value={block.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} rows={2} />
            </div>
          )}
          
          {/* Common style controls */}
          <div className="border-t pt-3 space-y-3">
            <Label className="text-xs font-medium text-muted-foreground">Style Options</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Text Align</Label>
                <Select value={block.styles.text_align || 'left'} onValueChange={(v) => updateStyles('text_align', v)}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Padding</Label>
                <Input value={block.styles.padding || '20px'} onChange={(e) => updateStyles('padding', e.target.value)} className="h-8" />
              </div>
            </div>
            {block.type !== 'spacer' && block.type !== 'divider' && (
              <div>
                <Label className="text-xs">Background Color</Label>
                <div className="flex gap-1">
                  <Input type="color" value={block.styles.background_color || '#ffffff'} onChange={(e) => updateStyles('background_color', e.target.value)} className="w-12 h-8 p-1" />
                  <Input value={block.styles.background_color || ''} onChange={(e) => updateStyles('background_color', e.target.value)} className="flex-1 h-8" placeholder="transparent" />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Email Designer Component
const EmailDesigner = ({ template, onChange, branding, mergeFields = DEFAULT_MERGE_FIELDS, previewVersion = 0 }) => {
  const [previewMode, setPreviewMode] = useState('desktop');
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const { token } = useAuth();
  
  const blocks = template.blocks || [];
  const globalStyles = template.global_styles || {};
  
  const addBlock = (type) => {
    const newBlock = getDefaultBlock(type);
    onChange({ ...template, blocks: [...blocks, newBlock] });
  };
  
  const updateBlock = (index, updatedBlock) => {
    const newBlocks = [...blocks];
    newBlocks[index] = updatedBlock;
    onChange({ ...template, blocks: newBlocks });
  };
  
  const deleteBlock = (index) => {
    const newBlocks = blocks.filter((_, i) => i !== index);
    onChange({ ...template, blocks: newBlocks });
  };
  
  const moveBlock = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
    onChange({ ...template, blocks: newBlocks });
  };
  
  const updateGlobalStyles = (key, value) => {
    onChange({ ...template, global_styles: { ...globalStyles, [key]: value } });
  };
  
  const loadPreview = async () => {
    if (!template.id) return;
    setLoadingPreview(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/email-designer/templates/${template.id}/preview`, {
        blocks: template.blocks || [],
        global_styles: template.global_styles || {},
        default_subject: template.default_subject || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewHtml(res.data.html);
    } catch (err) {
      toast.error('Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id, previewVersion]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
      {/* Left: Block Editor */}
      <div className="space-y-4">
        {/* Global Styles */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Palette className="h-4 w-4" />
              Global Styles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Background</Label>
                <div className="flex gap-1">
                  <Input type="color" value={globalStyles.background_color || '#f4f4f4'} onChange={(e) => updateGlobalStyles('background_color', e.target.value)} className="w-10 h-8 p-1" />
                  <Input value={globalStyles.background_color || '#f4f4f4'} onChange={(e) => updateGlobalStyles('background_color', e.target.value)} className="flex-1 h-8" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Content BG</Label>
                <div className="flex gap-1">
                  <Input type="color" value={globalStyles.content_background || '#ffffff'} onChange={(e) => updateGlobalStyles('content_background', e.target.value)} className="w-10 h-8 p-1" />
                  <Input value={globalStyles.content_background || '#ffffff'} onChange={(e) => updateGlobalStyles('content_background', e.target.value)} className="flex-1 h-8" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Text Color</Label>
                <div className="flex gap-1">
                  <Input type="color" value={globalStyles.text_color || '#333333'} onChange={(e) => updateGlobalStyles('text_color', e.target.value)} className="w-10 h-8 p-1" />
                  <Input value={globalStyles.text_color || '#333333'} onChange={(e) => updateGlobalStyles('text_color', e.target.value)} className="flex-1 h-8" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Link Color</Label>
                <div className="flex gap-1">
                  <Input type="color" value={globalStyles.link_color || '#92298d'} onChange={(e) => updateGlobalStyles('link_color', e.target.value)} className="w-10 h-8 p-1" />
                  <Input value={globalStyles.link_color || '#92298d'} onChange={(e) => updateGlobalStyles('link_color', e.target.value)} className="flex-1 h-8" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Font Family</Label>
              <Select value={globalStyles.font_family || 'Arial, sans-serif'} onValueChange={(v) => updateGlobalStyles('font_family', v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Arial, sans-serif">Arial</SelectItem>
                  <SelectItem value="Georgia, serif">Georgia</SelectItem>
                  <SelectItem value="'Trebuchet MS', sans-serif">Trebuchet MS</SelectItem>
                  <SelectItem value="Verdana, sans-serif">Verdana</SelectItem>
                  <SelectItem value="'Times New Roman', serif">Times New Roman</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
        
        {/* Add Block */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Add Block</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-2">
              {BLOCK_TYPES.map((bt) => (
                <Button
                  key={bt.type}
                  data-testid={`add-block-${bt.type}`}
                  variant="outline"
                  size="sm"
                  className="h-16 flex-col gap-1"
                  onClick={() => addBlock(bt.type)}
                >
                  <bt.icon className="h-4 w-4" />
                  <span className="text-xs">{bt.label}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Blocks List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Content Blocks ({blocks.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              {blocks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No blocks yet. Add blocks above to build your email.</p>
                </div>
              ) : (
                blocks.map((block, index) => (
                  <BlockEditor
                    key={block.id}
                    block={block}
                    onChange={(updated) => updateBlock(index, updated)}
                    onDelete={() => deleteBlock(index)}
                    onMoveUp={() => moveBlock(index, -1)}
                    onMoveDown={() => moveBlock(index, 1)}
                    isFirst={index === 0}
                    isLast={index === blocks.length - 1}
                    branding={branding}
                    mergeFields={mergeFields}
                  />
                ))
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
      
      {/* Right: Preview */}
      <div className="space-y-4">
        <Card className="h-full">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Preview
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant={previewMode === 'desktop' ? 'default' : 'outline'}
                  size="sm"
                  data-testid="preview-desktop-btn"
                  onClick={() => setPreviewMode('desktop')}
                >
                  <Monitor className="h-4 w-4 mr-1" />
                  Desktop
                </Button>
                <Button
                  variant={previewMode === 'mobile' ? 'default' : 'outline'}
                  size="sm"
                  data-testid="preview-mobile-btn"
                  onClick={() => setPreviewMode('mobile')}
                >
                  <Smartphone className="h-4 w-4 mr-1" />
                  Mobile
                </Button>
                <Button variant="outline" size="sm" onClick={loadPreview} disabled={loadingPreview || !template.id} data-testid="preview-refresh-btn">
                  {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div 
              className={cn(
                "border rounded-lg overflow-hidden bg-gray-100 mx-auto transition-all",
                previewMode === 'mobile' ? 'max-w-[375px]' : 'max-w-[600px]'
              )}
              style={{ minHeight: '500px' }}
              data-testid={`email-preview-${previewMode}`}
            >
              {previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-[500px] border-0"
                  title="Email Preview"
                  data-testid="email-preview-iframe"
                />
              ) : (
                <div className="flex items-center justify-center h-[500px] text-muted-foreground">
                  <div className="text-center">
                    <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    {loadingPreview ? <Loader2 className="h-6 w-6 mx-auto animate-spin" /> : <p>Click Refresh to see the preview</p>}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main Page Component
const EmailDesignerTemplatesPage = () => {
  const { token, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mergeFields, setMergeFields] = useState(DEFAULT_MERGE_FIELDS);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({});
  const [filter, setFilter] = useState({ category: 'all', search: '' });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(0);
  
  const isAdmin = user?.role === 'system_admin' || user?.role === 'executive';

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/email-designer/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data.templates || []);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-designer/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to load categories');
    }
  }, [token]);

  const fetchBranding = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-designer/organization-branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranding(res.data);
    } catch (err) {
      console.error('Failed to load branding');
    }
  }, [token]);

  const fetchMergeFields = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/message-templates/merge-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Transform API response to component format
      const fields = (res.data || []).map(f => ({
        field: f.field,
        label: f.description || f.field.replace(/[{}]/g, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        isCustom: f.is_custom || false
      }));
      setMergeFields(fields.length > 0 ? fields : DEFAULT_MERGE_FIELDS);
    } catch (err) {
      console.error('Failed to load merge fields, using defaults');
      setMergeFields(DEFAULT_MERGE_FIELDS);
    }
  }, [token]);

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
    fetchBranding();
    fetchMergeFields();
  }, [fetchTemplates, fetchCategories, fetchBranding, fetchMergeFields]);

  const createTemplate = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/email-designer/templates`, {
        name: 'New Email Template',
        category: 'general',
        blocks: [
          getDefaultBlock('logo'),
          getDefaultBlock('header'),
          getDefaultBlock('text'),
          getDefaultBlock('button'),
          getDefaultBlock('divider'),
          getDefaultBlock('footer')
        ]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTemplate(res.data.template);
      setShowEditor(true);
      toast.success('Template created');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create template');
    }
  };

  const saveTemplate = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/email-designer/templates/${selectedTemplate.id}`, selectedTemplate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template saved');
      setPreviewVersion((v) => v + 1);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (template) => {
    try {
      const endpoint = template.is_active ? 'deactivate' : 'activate';
      await axios.put(`${API_URL}/api/admin/email-designer/templates/${template.id}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(template.is_active ? 'Template deactivated' : 'Template activated');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update template');
    }
  };

  const duplicateTemplate = async (template) => {
    try {
      await axios.post(`${API_URL}/api/admin/email-designer/templates/${template.id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template duplicated');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate template');
    }
  };

  const deleteTemplate = async () => {
    if (!deletingTemplate) return;
    try {
      await axios.delete(`${API_URL}/api/admin/email-designer/templates/${deletingTemplate.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      setShowDeleteDialog(false);
      setDeletingTemplate(null);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  const openTemplate = async (template) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-designer/templates/${template.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTemplate(res.data);
      setShowEditor(true);
    } catch (err) {
      toast.error('Failed to load template');
    }
  };

  const filteredTemplates = templates.filter(t => {
    if (filter.category !== 'all' && t.category !== filter.category) return false;
    if (filter.search && !t.name.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">Only System Administrators and Executives can manage Email Designer Templates.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showEditor && selectedTemplate) {
    return (
      <div className="p-6 h-[calc(100vh-100px)]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => { setShowEditor(false); setSelectedTemplate(null); }}>
              <ChevronRight className="h-4 w-4 rotate-180 mr-1" />
              Back
            </Button>
            <div>
              <Input
                value={selectedTemplate.name}
                onChange={(e) => setSelectedTemplate({ ...selectedTemplate, name: e.target.value })}
                className="text-lg font-semibold h-auto py-1 border-0 focus-visible:ring-0 px-0"
              />
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={selectedTemplate.is_active ? 'default' : 'secondary'}>
                  {selectedTemplate.is_active ? 'Active' : 'Inactive'}
                </Badge>
                <Select value={selectedTemplate.category} onValueChange={(v) => setSelectedTemplate({ ...selectedTemplate, category: v })}>
                  <SelectTrigger className="h-6 w-auto border-0 p-0 text-xs text-muted-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => toggleActive(selectedTemplate)}>
              {selectedTemplate.is_active ? 'Deactivate' : 'Activate'}
            </Button>
            <Button onClick={saveTemplate} disabled={saving} data-testid="save-email-design-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Template
            </Button>
          </div>
        </div>
        
        {/* Template Info */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="text-xs">Default Subject</Label>
            <Input
              value={selectedTemplate.default_subject || ''}
              onChange={(e) => setSelectedTemplate({ ...selectedTemplate, default_subject: e.target.value })}
              placeholder="Email subject line..."
            />
          </div>
          <div>
            <Label className="text-xs">Preheader Text</Label>
            <Input
              value={selectedTemplate.preheader || ''}
              onChange={(e) => setSelectedTemplate({ ...selectedTemplate, preheader: e.target.value })}
              placeholder="Preview text shown in inbox..."
            />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input
              value={selectedTemplate.description || ''}
              onChange={(e) => setSelectedTemplate({ ...selectedTemplate, description: e.target.value })}
              placeholder="Internal description..."
            />
          </div>
        </div>
        
        <EmailDesigner
          template={selectedTemplate}
          onChange={setSelectedTemplate}
          branding={branding}
          mergeFields={mergeFields}
          previewVersion={previewVersion}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Email Designer Templates</h1>
          <p className="text-muted-foreground">Create and manage reusable email templates</p>
        </div>
        <Button onClick={createTemplate} data-testid="create-email-design-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      <Tabs defaultValue="designs">
      <TabsList>
        <TabsTrigger value="designs" data-testid="email-designer-designs-tab">Designs</TabsTrigger>
        <TabsTrigger value="system" data-testid="email-designer-system-emails-tab">System Emails</TabsTrigger>
      </TabsList>
      <TabsContent value="designs" className="space-y-6 mt-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            className="pl-9"
          />
        </div>
        <Select value={filter.category} onValueChange={(v) => setFilter({ ...filter, category: v })}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No templates found</h3>
            <p className="text-muted-foreground mb-4">Create your first email template to get started.</p>
            <Button onClick={createTemplate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">{template.name}</CardTitle>
                    <CardDescription className="text-xs mt-1">
                      {categories.find(c => c.id === template.category)?.name || template.category}
                    </CardDescription>
                  </div>
                  <Badge variant={template.is_active ? 'default' : 'secondary'} className="ml-2">
                    {template.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {template.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{template.description}</p>
                )}
                <div className="text-xs text-muted-foreground mb-3">
                  Modified {template.modified_at ? format(new Date(template.modified_at), 'MMM d, yyyy') : 'N/A'}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="default" size="sm" className="flex-1" onClick={() => openTemplate(template)} data-testid={`edit-design-${template.id}`}>
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => duplicateTemplate(template)} data-testid={`duplicate-design-${template.id}`}>
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => toggleActive(template)} data-testid={`toggle-design-${template.id}`}>
                    {template.is_active ? <ToggleRight className="h-3 w-3" /> : <ToggleLeft className="h-3 w-3" />}
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-500" onClick={() => { setDeletingTemplate(template); setShowDeleteDialog(true); }} data-testid={`delete-design-${template.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </TabsContent>
      <TabsContent value="system" className="mt-4">
        <SystemEmailDesigns />
      </TabsContent>
      </Tabs>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deletingTemplate?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteTemplate}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailDesignerTemplatesPage;
